import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { Modal, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx-js-style';
import ReportsService from '../../../Services/reports.service';
import { useHamburgerMenu } from '../../../Contexts/HamburgerMenuContext';
import { toast } from 'react-toastify';
import { PermissionsContext } from '../../../Contexts/Permissions';
import SignatureModal from '../../Widgets/SignatureModal/SignatureModal';
import Swal from 'sweetalert2';
import ProjectsService from '../../../Services/projects.service';

// Import composition components
import ReportHeader from './ReportHeader';
import ReportToolbar from './ReportToolbar';
import PeriodSelectionModal from './PeriodSelectionModal';
import MonthlyReportModal from './MonthlyReportModal';
import TransactionTable from './TransactionTable';
import TableTotals from './TableTotals';
import ReportCurrencySelector from '../ReportCurrencySelector/ReportCurrencySelector';
import ReportLanguageSelector from '../ReportLanguageSelector/ReportLanguageSelector';
import { useReportTranslation } from '../../../hooks/useReportTranslation';
import Constants from '../../../assests/Constants/constants';

// Import custom hooks
import { useProfitAndLossData } from '../../../hooks/useProfitAndLossData';
import { usePeriodComparison } from '../../../hooks/usePeriodComparison';

// Import utilities
import { groupBy, formatDateRange } from '../../../Utils/ReportUtils';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { applyWorkbookRtl, AMOUNT_NUMBER_FORMAT } from '../../../Utils/ExcelReport';
import { applyPartnersEquitySection } from '../../../Utils/partners/partnersEquityDistribution';
import { appendPartnersEquityToSheet } from '../../../Utils/partners/partnersEquityExcel';

/** Define the modal style */
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '92%',
  maxWidth: '1280px',
  minWidth: '900px',
  height: '90%',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '10px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '12px',
  boxSizing: 'border-box',
};

/** Number of transactions per page before inserting a new .pageStyle block */
const transactionsPerPage = 13;

const compareProjectCodes = (a, b) =>
  String(a?.code ?? '').localeCompare(String(b?.code ?? ''), undefined, { numeric: true });

const escapeHtmlAttr = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const ProfitAndLossReportHtmlRefactored = ({
  open,
  handleClose,
  data,
  handleSelect,
  token,
  client,
  showAccountCardsWithZeros,
  setShowAccountCardsWithZeros,
  // Additional props for MonthlyReportModal
  palCodesData,
  palDates,
  onTrialBalanceSegmentationFilterChange,
  // Optional: allow parent to control showCards/showPercentageColumn (for matching modal)
  externalShowCards,
  externalShowPercentageColumn,
  onHtmlContentChange, // Callback to expose HTML content
  onProfitAndLossTotalChange, // Callback to expose profitAndLossTotalBalance1
  isMatchingReport = false,
  // Foreign-currency selection (null = Shekel, 'MIXED' = all currencies, number = single currency)
  selectedCurrency = null,
  availableCurrencies = [],
  onCurrencyChange,
  partners = [],
  // Hides the tools that refetch live data (monthly report, period comparison, projects,
  // currency), for callers that pass an adjusted data set such as the annual report.
  hideLiveDataTools = false,
}) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();

  /* ---- Foreign-currency (מט"ח) display ---- */
  const showFc = selectedCurrency !== null && selectedCurrency !== undefined;
  const fcOnly = typeof selectedCurrency === 'number';
  const fcLabel = (() => {
    if (typeof selectedCurrency === 'number') {
      const codeName = Object.entries(Constants.ForeignCurrencyCode || {})
        .find(([, value]) => value === selectedCurrency)?.[0];
      const sym = codeName ? (Constants.ForeignCurrencySymbol?.[codeName] || codeName) : '';
      return sym ? `מט"ח (${sym})` : 'מט"ח';
    }
    return 'מט"ח';
  })();
  const fcRowHasActivity = (tr) =>
    (Number(tr?.foreignCurrencyDebit) || 0) !== 0 ||
    (Number(tr?.foreignCurrencyCredit) || 0) !== 0 ||
    (Number(tr?.foreignCurrencyBalance) || 0) !== 0;

  const noopSegmentationChange = useCallback(() => {}, []);
  const notifySegmentationFilterChange = onTrialBalanceSegmentationFilterChange ?? noopSegmentationChange;

  const [byProjectsFilterActive, setByProjectsFilterActive] = useState(false);
  const [segmentationProjects, setSegmentationProjects] = useState([]);
  const [loadingSegmentations, setLoadingSegmentations] = useState(false);
  const [selectedFromSegmentation, setSelectedFromSegmentation] = useState(null);
  const [selectedToSegmentation, setSelectedToSegmentation] = useState(null);

  const projectsProfitAndLossApiFilter = useMemo(() => {
    if (!byProjectsFilterActive) return null;
    const payload = { trialBalanceType: 'Projects' };
    if (selectedFromSegmentation?.id) {
      payload.fromSegmentationId = selectedFromSegmentation.id;
    }
    if (selectedToSegmentation?.id) {
      payload.toSegmentationId = selectedToSegmentation.id;
    }
    return payload;
  }, [byProjectsFilterActive, selectedFromSegmentation, selectedToSegmentation]);
  
  // Chat functionality
  const { handleSendMessageToChat, selectUserAndOpenChat } = useHamburgerMenu();
  const { userTypeContext, permissionsState } = useContext(PermissionsContext);

  // State for UI controls
  const [showCards, setCards] = useState(true);
  const [showAccountCodeId, setShowAccountCodeId] = useState(true);
  const [showPercentageColumn, setShowPercentageColumn] = useState(false);
  const [reportHtmlContent, setReportHtmlContent] = useState('');
  const {
    reportLanguage,
    setReportLanguage,
    isTranslating,
    displayHtml,
    previewDir,
    translateHtml,
    translateWb,
  } = useReportTranslation(reportHtmlContent, token);
  const [clientData, setClientData] = useState('');

  // Allow parent (LedgerReport matching modal) to control these flags,
  // so toggling checkboxes regenerates the HTML the same way as ReportToolbar.
  useEffect(() => {
    if (typeof externalShowCards === 'boolean') {
      setCards(externalShowCards);
    }
  }, [externalShowCards]);

  useEffect(() => {
    if (typeof externalShowPercentageColumn === 'boolean') {
      setShowPercentageColumn(externalShowPercentageColumn);
    }
  }, [externalShowPercentageColumn]);

  // Expose HTML content to parent component via callback (for matching modal)
  useEffect(() => {
    if (onHtmlContentChange && reportHtmlContent) {
      onHtmlContentChange(reportHtmlContent);
    }
  }, [reportHtmlContent, onHtmlContentChange]);
  
  // State for Monthly Report Modal
  const [showMonthlyReportModal, setShowMonthlyReportModal] = useState(false);

  // Signature state ('chat' | 'download')
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signaturePurpose, setSignaturePurpose] = useState('chat');

  // Use custom hooks - usePeriodComparison must come first since useProfitAndLossData depends on it
  const {
    showPeriodComparison,
    periodComparisonData,
    showPeriodSelectionModal,
    selectedPeriod1,
    selectedPeriod2,
    isLoadingComparison,
    setSelectedPeriod1,
    setSelectedPeriod2,
    handleOpenPeriodSelection,
    handleClosePeriodSelection,
    handleFetchComparisonData,
    handleCloseComparison
  } = usePeriodComparison(client, token, true, projectsProfitAndLossApiFilter); // true for isProfitAndLoss since this is ProfitAndLoss

  const {
    profitAndLossNames,
    filteredTransactions,
    comparisonTransactions,
    totalIncome,
    comparisonTotalIncome,
    uniqueCategories,
    showReport,
    setShowReport,
    rebuildProfitAndLossNames
  } = useProfitAndLossData(data, showAccountCardsWithZeros, showPeriodComparison, periodComparisonData);

  // Summation variables for the final rows
  let profitAndLossTotalInitDebit = 0;
  let profitAndLossTotalInitCredit = 0;
  let profitAndLossTotalDebit = 0;
  let profitAndLossTotalCredit = 0;
  let profitAndLossTotalBalance1 = 0;
  // Foreign-currency P&L accumulators.
  let profitAndLossTotalFcDebit = 0;
  let profitAndLossTotalFcCredit = 0;
  let profitAndLossTotalFcBalance = 0;
  
  // Comparison period summation variables
  let comparisonProfitAndLossTotalInitDebit = 0;
  let comparisonProfitAndLossTotalInitCredit = 0;
  let comparisonProfitAndLossTotalDebit = 0;
  let comparisonProfitAndLossTotalCredit = 0;
  let comparisonProfitAndLossTotalBalance = 0;
  let comparisonProfitAndLossTotalFcDebit = 0;
  let comparisonProfitAndLossTotalFcCredit = 0;
  let comparisonProfitAndLossTotalFcBalance = 0;

  // Comparison period total report variables (equivalent to totalXXXAccountReport)
  let comparisonTotalInitDebitAccountReport = 0;
  let comparisonTotalInitCreditAccountReport = 0;
  let comparisonTotalDebitAccountReport = 0;
  let comparisonTotalCreditAccountReport = 0;
  let comparisonTotalBalanceReport = 0;

  // Global totals
  let totalInitDebitAccountReport = 0;
  let totalInitCreditAccountReport = 0;
  let totalDebitAccountReport = 0;
  let totalCreditAccountReport = 0;
  let totalBalanceReport = 0;

  // Keep track of which palCodeAccountGroups are in P&L
  const reportGenerated = useRef(false);

  // Generate the report when triggered
  useEffect(() => {
    // Generate if:
    // 1. Component is open (normal modal)
    // 2. OR we're being used for HTML generation (matching modal) - always generate, even with no data
    if (open || onHtmlContentChange) {
      generateReportHtml();
    }
    if (showReport) {
      setShowReport(false);
    }
  }, [
    showReport,
    showAccountCardsWithZeros,
    showCards,
    showAccountCodeId,
    showPercentageColumn,
    showPeriodComparison,
    periodComparisonData,
    open,
    onHtmlContentChange,
    filteredTransactions.length,
    byProjectsFilterActive,
    selectedFromSegmentation,
    selectedToSegmentation,
    isMatchingReport,
    partners,
  ]);

  // Keep track of the client prop if needed
  useEffect(() => {
    setClientData(client);
  }, [client]);

  /** 
   * Listen for custom "rowClick" events; decode the JSON 
   * and call handleSelect with the relevant info
   */
  useEffect(() => {
    const handleRowClick = (event) => {
      try {
        const jsonString = decodeURIComponent(event.detail);
        const transaction = JSON.parse(jsonString);

        const accountCard = {
          cardId: transaction.accountCardId,
          name: transaction.accountCardName,
        };

        const palCode = {
          palCodeId: transaction.palCode,
        };

        handleSelect(accountCard, palCode);
      } catch (error) {
        console.error("Failed to parse JSON from event.detail:", error);
      }
    };

    window.addEventListener('rowClick', handleRowClick);
    return () => {
      window.removeEventListener('rowClick', handleRowClick);
    };
  }, [handleSelect]);

  /**
   * Generate the main report HTML
   */
  const generateReportHtml = () => {
    // Reset totals at the start of generation
    profitAndLossTotalBalance1 = 0;
    comparisonProfitAndLossTotalBalance = 0;
    profitAndLossTotalFcDebit = 0;
    profitAndLossTotalFcCredit = 0;
    profitAndLossTotalFcBalance = 0;
    comparisonProfitAndLossTotalFcDebit = 0;
    comparisonProfitAndLossTotalFcCredit = 0;
    comparisonProfitAndLossTotalFcBalance = 0;
    totalInitDebitAccountReport = 0;
    totalInitCreditAccountReport = 0;
    totalDebitAccountReport = 0;
    totalCreditAccountReport = 0;
    totalBalanceReport = 0;
    comparisonTotalInitDebitAccountReport = 0;
    comparisonTotalInitCreditAccountReport = 0;
    comparisonTotalDebitAccountReport = 0;
    comparisonTotalCreditAccountReport = 0;
    comparisonTotalBalanceReport = 0;

    // If no data, return empty report HTML (same as server-side GetEmptyReportHtml)
    if (!filteredTransactions || filteredTransactions.length === 0) {
      const emptyHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Profit and Loss Report</title>
    <style>
        body{
            font-family:Arial,Helvetica,sans-serif;
            display:flex;justify-content:center;align-items:center;
            height:100vh;margin:0;background:#f4f4f4}
        .message{text-align:center;color:#333}
        .message h1{color:navy}
    </style>
</head>
<body>
    <div class="message">
        <h1>דווח רווח והפסד</h1>
        <p>אין נתונים להציג בתאריך המובחר</p>
    </div>
</body>
</html>`;
      setReportHtmlContent(emptyHtml);
      // Expose the total (0 in this case)
      if (onProfitAndLossTotalChange) {
        onProfitAndLossTotalChange(0);
      }
      return;
    }

    // Determine which data to use for the first period
    let firstPeriodData, secondPeriodData;
    
    if (showPeriodComparison && periodComparisonData.data1 && periodComparisonData.data2) {
      firstPeriodData = periodComparisonData.data1;
      secondPeriodData = periodComparisonData.data2;
    } else {
      firstPeriodData = data;
      secondPeriodData = null;
    }

    // Generate header content
    const headerContent = ReportHeader({
      firstPeriodData,
      secondPeriodData,
      showPeriodComparison,
      uniquePalCodeCategories: uniqueCategories.uniquePalCodeCategories,
      uniquePalCodes: uniqueCategories.uniquePalCodes,
      formatDateRange,
      byProjectsFilterActive,
      selectedFromSegmentation,
      selectedToSegmentation,
      t,
      escapeHtmlAttr,
      caseType: client?.caseType ?? data?.caseType,
    });

    // When a single currency is selected, only show rows with activity in that currency.
    const sectionTransactions = typeof selectedCurrency === 'number'
      ? filteredTransactions.filter(fcRowHasActivity)
      : filteredTransactions;
    const sectionComparisonTransactions = (typeof selectedCurrency === 'number' && comparisonTransactions)
      ? comparisonTransactions.filter(fcRowHasActivity)
      : comparisonTransactions;

    // Generate main content sections
    const sectionContent = generateAccountingGroupClassificationSections(
      sectionTransactions,
      uniqueCategories.uniquePalCodeCategories,
      uniqueCategories.uniquePalCodes,
      sectionComparisonTransactions,
      comparisonTotalIncome,
      firstPeriodData,
      secondPeriodData
    );

    // Build the final row we want to inject
    const finalSummaryRow = TableTotals.generateFinalSummaryRow(
      profitAndLossTotalBalance1,
      comparisonProfitAndLossTotalBalance,
      showPercentageColumn,
      showPeriodComparison,
      totalIncome,
      comparisonTotalIncome,
      {
        showFc,
        showCards,
        fcOnly,
        plFc: {
          debit: profitAndLossTotalFcDebit,
          credit: profitAndLossTotalFcCredit,
          balance: profitAndLossTotalFcBalance,
        },
        compPlFc: {
          debit: comparisonProfitAndLossTotalFcDebit,
          credit: comparisonProfitAndLossTotalFcCredit,
          balance: comparisonProfitAndLossTotalFcBalance,
        },
      }
    );

    // Split the content so we can inject the final row
    let content = headerContent + sectionContent;
    const lastIndex = content.lastIndexOf('<tr class="tr-final-row-palcode-category">');

    if (lastIndex !== -1) {
      const endIndex = content.indexOf('</tr>', lastIndex) + 5;
      content = content.slice(0, lastIndex) + content.slice(endIndex);
    }

    // Find the last </tbody> and insert the final summary row before it
    const lastTbodyIndex = content.lastIndexOf('</tbody>');
    if (lastTbodyIndex !== -1) {
      content = content.slice(0, lastTbodyIndex) + finalSummaryRow + content.slice(lastTbodyIndex);
    } else {
      // Fallback: if no tbody found, append before closing tags
      content = content.slice(0, -31);
      content += finalSummaryRow + "</tbody></table></div>";
    }

    // Wrap in HTML document with styles
    const reportHtml = generateFullHtmlDocument(content);
    setReportHtmlContent(applyPartnersEquitySection(reportHtml, partners, profitAndLossTotalBalance1));
    
    // Expose profitAndLossTotalBalance1 to parent (for matching modal)
    if (onProfitAndLossTotalChange) {
      onProfitAndLossTotalChange(profitAndLossTotalBalance1);
    }
  };

  /**
   * Generate full HTML document with styles
   */
  const generateFullHtmlDocument = (content) => {
    const percentageColumnClass = showPercentageColumn ? '' : ' pl-no-percentage-column';
    const matchingReportClass = isMatchingReport ? ' pl-matching-report' : '';
    const showCardsClass = showCards ? ' pl-mode-accountcards' : '';
    return `
      <!DOCTYPE html>
      <html lang="he">
      <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
          ${getReportStyles()}
          </style>
      </head>
      <body dir="rtl">
          <div class="content-wrapper${percentageColumnClass}${matchingReportClass}${showCardsClass}">
              ${content}
          </div>
      </body>
      </html>
    `;
  };

  /**
   * Get all CSS styles for the report
   */
  const getReportStyles = () => {
    // Landscape once the extra foreign-currency columns (detailed = 3 per period) or
    // period comparison make the table too wide for portrait A4.
    const useLandscape = (fcOnly || showFc) && (showCards || showPeriodComparison);
    return `
      * {
        box-sizing: border-box;
        font-family: Arial, Helvetica, sans-serif;
      }

      html, body {
        margin: 0;
        padding: 0;
        background: #ffffff;
        color: #1a1a1a;
        direction: rtl;
      }

      body {
        counter-reset: page;
        font-size: 13px;
        line-height: 1.4;
      }

      @page {
        size: ${useLandscape ? 'A4 landscape' : 'A4'};
        margin: 8mm 10mm 16mm 10mm;
        @top-left {
          content: "עמוד " counter(page) " מתוך " counter(pages);
          font-size: 10px;
          font-weight: 700;
          color: #333;
          vertical-align: bottom;
        }
      }

      .page-number::before { content: counter(page); }
      .total-pages::before { content: counter(pages); }

      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }

      .content-wrapper {
        border: none;
        border-radius: 0;
        box-shadow: none;
        background: #ffffff;
        padding: 0;
        margin: 0;
        width: 100%;
        overflow: visible;
      }

      .pageStyle {
        display: block;
        background: #ffffff;
        padding: 0;
        margin: 0 0 8px 0;
        page-break-inside: avoid;
      }

      .page-break {
        page-break-before: always;
      }

      .hoverTr {
        cursor: pointer;
      }

      .hoverTr:hover {
        background-color: #f7f7f7;
      }

      .content-wrapper.pl-mode-accountcards .hoverTr {
        transition: transform 0.2s ease, box-shadow 0.1s ease;
      }

      .content-wrapper.pl-mode-accountcards .hoverTr:hover {
        background-color: transparent;
        transform: translateY(-5px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      }

      @media print {
        @page {
          size: ${useLandscape ? 'A4 landscape' : 'A4'};
          margin: 8mm 10mm 16mm 10mm !important;
          @top-left {
            content: "עמוד " counter(page) " מתוך " counter(pages);
            font-size: 10px;
            font-weight: 700;
            color: #333;
            vertical-align: bottom;
          }
        }

        html,
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          background: #ffffff !important;
          overflow: visible !important;
          direction: rtl;
        }

        .content-wrapper {
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          overflow: visible !important;
          border: none !important;
          box-shadow: none !important;
        }

        .pageStyle {
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          display: block !important;
          page-break-inside: auto !important;
        }

        .report-page-header,
        .report-header-row,
        .report-meta-details,
        .combined-section-header,
        .section-header-block {
          width: 100% !important;
          max-width: 100% !important;
          overflow: visible !important;
          box-sizing: border-box;
        }

        .hoverTr {
          cursor: default;
          background: transparent !important;
          transform: none !important;
          box-shadow: none !important;
        }

        .hoverTr:hover {
          background: transparent !important;
        }

        .page-break {
          display: none;
        }

        .table {
          width: 100% !important;
          max-width: 100% !important;
          font-size: 12px !important;
          border-collapse: collapse !important;
          border-spacing: 0 !important;
          table-layout: fixed !important;
        }

        .table th,
        .table td {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          overflow: visible !important;
          word-wrap: break-word;
          overflow-wrap: anywhere;
          padding: 3px 4px !important;
        }

        .table col.col-numeric {
          width: 12% !important;
        }

        .table.pl-period-comparison.pl-cols-dual col.col-account {
          width: 32% !important;
        }
        .table.pl-period-comparison.pl-cols-quad col.col-account {
          width: 32% !important;
        }



        .table.pl-period-comparison .tr-header-period-row,
        .table.pl-period-comparison .tr-header-sub-row {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }

        .table.pl-period-comparison .td-header-period,
        .table.pl-period-comparison .td-header-sub-amount,
        .table.pl-period-comparison .td-header-sub-pct {
          overflow: hidden !important;
          position: static !important;
        }

        .palcodes-details {
          font-size: 11px !important;
          line-height: 1.25 !important;
        }

        .report-title { font-size: 22px !important; }
        .report-date-range { font-size: 12px !important; }
        .report-client-name { font-size: 14px !important; }
        .report-client-case,
        .report-print-datetime { font-size: 11px !important; }

        .pl-matching-report .report-title { font-size: 18px !important; }
        .pl-matching-report .report-date-range { font-size: 11px !important; }
        .pl-matching-report .report-client-name { font-size: 13px !important; }
        .pl-matching-report .report-client-case,
        .pl-matching-report .report-print-datetime { font-size: 11px !important; }

        .sums-of-transactions,
        .section-header-title { font-size: 13px !important; }

        .section-header-ag-label,
        .sums-of-transactions.section-header-ag-label {
          font-size: 13px !important;
        }

        .pl-matching-report .sums-of-transactions,
        .pl-matching-report .section-header-title {
          font-size: 12px !important;
        }

        .pl-matching-report .sums-of-transactions.section-header-ag-label {
          font-size: 12px !important;
        }

        .pl-matching-report .table thead th,
        .pl-matching-report .td-header,
        .pl-matching-report .td-header-1 {
          font-size: 11px !important;
        }

        .header-label-value { font-size: 12px !important; }
        .overflowed { font-size: 12px !important; }

        .td-header,
        .td-header-1 { font-size: 12px !important; }

        .pl-no-percentage-column .td-percentage {
          display: none !important;
          width: 0 !important;
          min-width: 0 !important;
          max-width: 0 !important;
          padding: 0 !important;
          border: none !important;
        }

        .pl-no-percentage-column tr.tr-final-row-palcode td {
          border-top: none !important;
          border-bottom: none !important;
        }

        td, .td-last, .td-amount-cell, .td-percentage {
          font-size: 12px !important;
        }

        .total-palcode { font-size: 12px !important; }
        .total-palcode-final { font-size: 13px !important; }

        .accounting-amount-underline-double,
        .accounting-amount-underline-triple,
        .accounting-underline-line {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        .accounting-amount-underline-double,
        .accounting-amount-underline-triple {
          min-width: 80px !important;
          width: auto !important;
          max-width: 100%;
        }

        .pl-matching-report .accounting-amount-underline-double,
        .pl-matching-report .accounting-amount-underline-triple {
          min-width: 60px !important;
        }

        .accounting-underline-lines {
          width: 100%;
        }

        .sum-details {
          margin-top: 4px !important;
          margin-bottom: 4px !important;
        }

        .report-date-comparison-block {
          flex-wrap: nowrap !important;
        }

        .report-date-comparison-block .report-date-range {
          width: auto !important;
          display: inline-block !important;
        }
      }

      ${getAdditionalStyles()}
    `;
  };

  /**
   * Get additional CSS styles
   */
  const getAdditionalStyles = () => {
    return `
      /* ── Report header ── */
      .report-page-header {
        margin-bottom: 4px;
      }

      .report-header-row {
        display: flex;
        flex-direction: row;
        direction: rtl;
        justify-content: space-between;
        align-items: flex-start;
        gap: 16px;
        padding: 4px 0 4px;
        width: 100%;
      }

      .report-header-title-block {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        direction: rtl;
        text-align: right;
        flex: 1;
      }

      .report-title {
        margin: 0 0 6px 0;
        padding: 0;
        font-size: 24px;
        font-weight: 700;
        color: #000;
        line-height: 1.2;
        text-decoration: none;
        text-align: right;
        width: 100%;
        align-self: flex-start;
      }

      .report-date-range {
        margin: 0;
        padding: 0;
        font-size: 13px;
        font-weight: 600;
        color: #222;
        text-decoration: none;
        text-align: right;
        width: 100%;
        align-self: flex-start;
      }

      .report-header-client-block {
        text-align: left;
        direction: rtl;
        flex: 1;
        max-width: 45%;
        align-self: flex-start;
      }

      .report-client-name {
        margin: 0 0 4px 0;
        padding: 0;
        font-size: 15px;
        font-weight: 700;
        color: #000;
        text-align: left;
      }

      .report-client-case,
      .report-print-datetime {
        margin: 0 0 2px 0;
        padding: 0;
        font-size: 13px;
        font-weight: 600;
        color: #333;
        text-align: left;
      }

      .pl-matching-report .report-title {
        font-size: 16px;
      }

      .pl-matching-report .report-date-range {
        font-size: 11px;
      }

      .pl-matching-report .report-client-name {
        font-size: 13px;
      }

      .pl-matching-report .report-client-case,
      .pl-matching-report .report-print-datetime {
        font-size: 11px;
      }

      .report-header-divider,
      .report-section-divider {
        border: none;
        border-top: 2px solid #000;
        margin: 4px 0 8px;
        width: 100%;
      }

      .report-section-divider {
        border-top-width: 1px;
        margin: 8px 0;
      }

      .report-meta-details {
        margin-top: 8px;
      }

      .report-date-comparison-block {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        justify-content: flex-start;
        direction: rtl;
        flex-wrap: nowrap;
        width: 100%;
        gap: 16px;
      }

      .report-date-comparison-block .report-date-range,
      .report-date-comparison-block .report-comparison-period {
        width: auto;
        flex: 0 0 auto;
        align-self: auto;
        display: inline-block;
        margin: 0;
        white-space: nowrap;
      }

      .report-comparison-period {
        font-weight: 600;
      }

      .report-project-filter {
        margin-top: 4px;
        font-weight: 600;
      }

      .report-project-range {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        flex-direction: row;
        direction: rtl;
      }

      /* ── Section headers ── */
      .accountingGroupClassification_accountingGroup_container {
        display: block;
        width: 100%;
        margin: 0;
      }

      .section-header-block {
        width: 100%;
        margin: 6px 0 4px;
        padding: 0 0 6px;
        box-sizing: border-box;
        border-bottom: 2px solid #000;
      }

      .section-header-row {
        display: flex;
        flex-direction: row;
        direction: rtl;
        justify-content: space-between;
        align-items: baseline;
        gap: 20px;
        flex-wrap: nowrap;
        width: 100%;
        box-sizing: border-box;
      }

      .section-header-row .sums-of-transactions {
        margin: 0;
        padding: 4px 0;
      }

      .section-header-row .section-header-classification,
      .section-header-row .section-header-group {
        text-align: right;
        flex-shrink: 0;
      }

      .section-header-row .section-header-account-group,
      .section-header-row .section-header-category {
        text-align: left;
        direction: rtl;
        flex-shrink: 0;
      }

      .combined-section-header {
        width: 100%;
        display: flex;
        flex-direction: column;
        direction: rtl;
        align-items: flex-start;
      }

      .section-header-ag-label {
        width: 100%;
        text-align: right;
        margin: 0;
        padding: 2px 0;
        font-size: 14px;
        font-weight: 700;
        color: #000;
      }

      .section-header-detail-label {
        width: 100%;
        text-align: right;
        margin: 0;
        padding: 2px 0;
      }

      .combined-section-header-continued {
        margin-top: 4px;
      }

      .sums-of-transactions,
      .section-header-title {
        font-size: 16px;
        font-weight: 700;
        color: #000;
        text-align: right;
        text-decoration: none;
        margin: 0;
      }

      .sums-of-transactions.section-header-ag-label {
        font-size: 18px;
        line-height: 1.2;
      }

      .pl-matching-report .sums-of-transactions,
      .pl-matching-report .section-header-title {
        font-size: 14px;
      }

      .pl-matching-report .sums-of-transactions.section-header-ag-label {
        font-size: 16px;
      }

      .pl-matching-report .table thead th {
        font-size: 13px;
      }

      .palcodes-details {
        font-size: 12px;
        color: #333;
        text-align: right;
        margin: 2px 0;
      }

      .sum-details {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-end;
        margin-top: 4px;
        margin-bottom: 4px;
      }

      /* ── Table layout ── */
      .table {
        width: 100%;
        border-collapse: collapse;
        border-spacing: 0;
        margin: 0 0 12px 0;
        page-break-inside: auto;
        font-size: 13px;
        table-layout: fixed;
      }

      .table col.col-account {
        width: auto;
      }

      .table col.col-numeric {
        width: 14%;
      }

      .table th,
      .table td {
        border-left: none;
        border-right: none;
        border-top: none;
        padding: 4px 8px;
        vertical-align: middle;
        text-align: right;
        background: #ffffff;
      }

      .table thead th {
        border-bottom: 1px solid #000;
        font-weight: 700;
        font-size: 13px;
        background: #ffffff;
      }

      .table thead .tr-header-period-row .td-header-period,
      .table thead .td-header-account {
        border-bottom: none !important;
      }

      .table th:first-child,
      .table td:first-child {
        text-align: right;
        word-wrap: break-word;
        overflow-wrap: anywhere;
      }

      .td-header,
      .td-header-1 {
        font-size: 13px;
        font-weight: 700;
        text-align: right !important;
        text-decoration: none;
        color: #000;
        border-bottom: 1px solid #000;
        padding-bottom: 4px;
      }

      .td-header-1 {
        width: auto;
      }

      .td-header {
        width: auto;
      }

      .td-header-period {
        border-bottom: none !important;
        padding: 4px 8px 2px;
        white-space: nowrap;
        text-align: right !important;
        text-decoration: none !important;
        font-weight: 700;
        vertical-align: bottom;
        direction: rtl;
      }

      .td-header-account {
        border-bottom: none !important;
        text-align: right !important;
        vertical-align: bottom;
        text-decoration: none;
      }

      .tr-header-sub-row .td-header-sub-amount,
      .tr-header-sub-row .td-header-sub-pct {
        border-bottom: none !important;
        padding: 3px 8px 4px;
      }

      .header-label-underline {
        display: block;
        width: 100%;
        min-width: 0;
        box-sizing: border-box;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .header-label-value {
        display: block;
        font-size: 13px;
        line-height: 1.25;
        white-space: nowrap;
      }

      .table.pl-period-comparison .header-label-underline {
        text-align: right;
      }


      .td-header-sub-amount {
        text-align: right !important;
        white-space: nowrap;
        vertical-align: top;
        direction: rtl;
      }

      .td-header-sub-pct {
        text-align: center !important;
        white-space: nowrap;
        vertical-align: top;
        direction: ltr;
      }

      /* Comparison mode — align headers with amount / % columns */
      .table.pl-period-comparison .td-amount-cell,
      .table.pl-period-comparison .td-last,
      .table.pl-period-comparison .td-last-sum,
      .table.pl-period-comparison .td-last-sum-1 {
        text-align: right !important;
        direction: ltr;
        padding-left: 8px;
        padding-right: 8px;
      }


      .table.pl-period-comparison .td-header-period,
      .table.pl-period-comparison .td-header-sub-amount {
        text-align: right !important;
      }




      .overflowed {
        font-size: 13px;
        font-weight: 700;
        color: #000;
        text-decoration: none;
        padding-top: 8px;
        padding-bottom: 4px;
        background: #fafafa;
      }

      .td-last,
      .td-amount-cell,
      .td-percentage,
      .td-last-sum,
      .td-last-sum-1 {
        font-size: 12px;
        text-align: left;
        direction: ltr;
        white-space: nowrap;
      }

      .td-account-cell,
      .table td:first-child.total-palcode,
      .table td:first-child.total-palcode-final {
        font-size: 12px;
        direction: rtl;
        text-align: right;
        white-space: normal;
      }

      @media screen {
        .table.pl-single-period col.col-account {
          width: 34%;
        }

        .table.pl-single-period col.col-numeric {
          width: 16%;
        }

        .table.pl-single-period th:first-child,
        .table.pl-single-period td:first-child,
        .table.pl-single-period .td-header-1 {
          width: 100%;
          min-width: 34%;
          max-width: 85%;
          box-sizing: border-box;
        }

        .table.pl-single-period .td-account-cell,
        .table.pl-single-period td:first-child.total-palcode,
        .table.pl-single-period td:first-child.total-palcode-final {
          width: 16%;
          min-width: 16%;
          max-width: 16%;
          padding-left: 4px;
          font-size: 11px;
          line-height: 1.25;
        }

        .table.pl-single-period .td-amount-cell,
        .table.pl-single-period .td-last-sum,
        .table.pl-single-period .td-last-sum-1,
        .table.pl-single-period .td-last,
        .table.pl-single-period .td-header:not(.td-header-1) {
          text-align: right !important;
        }

        .table.pl-single-period .td-percentage {
          text-align: right !important;
        }

        .table.pl-single-period .total-palcode {
          font-size: 11px;
        }

        .table.pl-single-period .total-palcode-final {
          font-size: 11px;
        }
      }

      .td-amount-cell {
        padding: 3px 8px;
      }

      .td-percentage {
        text-align: center;
        direction: ltr;
      }

      /* ── Row types: borders on cells only (continuous collapsed lines) ── */
      .tr {
        font-size: 13px;
      }

      tr.tr-final-row-palcode td {
        font-weight: 700;
        padding-top: 6px;
        padding-bottom: 6px;
      }

      .pl-no-percentage-column .td-percentage {
        display: none;
        width: 0 !important;
        min-width: 0 !important;
        max-width: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        border: none !important;
        overflow: hidden;
      }



      tr.tr-final-row-palcode-category td {
        font-weight: 700;
        padding-top: 6px;
        padding-bottom: 6px;
      }

      tr.ag-classification-total-row td {
        font-weight: 700;
      }

      tr.pal-code-account-group-total-row td {
        font-weight: 700;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .accounting-amount-underline-double,
      .accounting-amount-underline-triple {
        display: inline-block;
        min-width: 120px;
        text-align: right;
        vertical-align: bottom;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .pl-matching-report .accounting-amount-underline-double,
      .pl-matching-report .accounting-amount-underline-triple {
        min-width: 60px;
      }

      .accounting-amount-value {
        display: block;
        line-height: 1.25;
        white-space: nowrap;
      }

      .accounting-underline-lines {
        display: block;
        width: 100%;
        margin-top: 2px;
      }

      .accounting-underline-line {
        display: block;
        width: 100%;
        height: 0;
        border: none;
        border-top: 1px solid #000;
        margin: 0;
        padding: 0;
        line-height: 0;
        font-size: 0;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .accounting-underline-lines-double .accounting-underline-line + .accounting-underline-line {
        margin-top: 2px;
      }

      .accounting-underline-lines-triple .accounting-underline-line + .accounting-underline-line {
        margin-top: 2px;
      }

      tr.ag-classification-total-row .td-last {
        padding-bottom: 6px;
        vertical-align: bottom;
      }

      tr.ag-classification-total-row .td-percentage {
        padding-bottom: 6px;
        vertical-align: bottom;
      }

      tr.pal-code-account-group-total-row .td-last-sum {
        padding-bottom: 8px;
        vertical-align: bottom;
      }

      tr.pal-code-account-group-total-row .td-percentage {
        padding-bottom: 8px;
        vertical-align: bottom;
      }





      tr.tr-final-row-palcode-Report td {
        font-weight: 700;
        background-color: #e8e8e8;
        border-top: 2px solid #000;
        border-bottom: 3px double #000;
        padding-top: 8px;
        padding-bottom: 8px;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .total-palcode {
        font-weight: 700;
        font-size: 13px;
        color: #000;
      }

      .total-palcode-final {
        font-weight: 700;
        font-size: 14px;
        color: #000;
        text-decoration: none;
      }

      /* Amount cells in total rows — typography only, no partial borders */
      .td-last-sum,
      .td-last-sum-1 {
        text-align: left;
        direction: ltr;
        font-weight: 700;
        font-size: 13px;
        white-space: nowrap;
      }

      .td-last-sum::before,
      .td-last-sum::after,
      .td-last-sum-1::before,
      .td-last-sum-1::after,
      .num-total::before,
      .num-total::after {
        content: none;
        display: none;
      }

      .num-total {
        font-weight: 700;
        direction: ltr;
      }

      /* ── Legacy class compatibility ── */
      h1 { color: #000; text-align: right; }
      p { color: #333; }

      .rt1, .rt2, .det-small, .first, .det, .num, .normal-num, .underlined {
        color: #000;
      }

      .middle-details,
      .middle-details-period {
        display: flex;
        justify-content: flex-start;
        align-items: flex-start;
        padding: 4px 0;
      }

      .titles-details {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .upper-name-div,
      .left-header-info,
      .right-header-info,
      #header {
        display: none;
      }

      .td {
        border: none;
        padding: 4px 6px;
      }

      .underline {
        width: 100%;
        border-top: 1px solid #000;
        border-bottom: none;
      }

      .number-of-transactions,
      .number-total {
        font-weight: 700;
        text-decoration: none;
      }
    `;
  };

  // Nested grouping logic for accounting classification sections
  const generateAccountingGroupClassificationSections = (
    filteredTransactions,
    uniquePalCodeCategories,
    uniquePalCodes,
    comparisonTransactions = null,
    comparisonTotalIncome = 0,
    firstPeriodData = null,
    secondPeriodData = null
  ) => {
    const groupedByClassification = groupBy(filteredTransactions, 'accountingGroupClassification');
    let sectionContent = '';

    Object.keys(groupedByClassification).forEach(agClassification => {
      const transactionsForClass = groupedByClassification[agClassification];
      const groupedByCategory = groupBy(transactionsForClass, 'palCodeCategory');
      const palCodeAccountGroup = transactionsForClass[0]?.palCodeAccountGroup || '';

      const categoryKeys = Object.keys(groupedByCategory);
      categoryKeys.forEach((catKey, catIndex) => {
        const transactionsForCat = groupedByCategory[catKey];
        const isLastCategoryInGroup = catIndex === categoryKeys.length - 1;

        const categoryLabel = catKey === 'NonPalCodeCategory' ? t('NonPalCodeCategory') : catKey;
        const agLabel = agClassification === 'NonAccountingGroupClassifaction'
            ? t('NonAccountingGroupClassifaction')
            : agClassification;
        const categoryAccountGroup = transactionsForCat[0]?.palCodeAccountGroup || palCodeAccountGroup;
        const groupLabel = categoryAccountGroup === 'NonPalCodeAccountGroup'
            ? t('NonPalCodeAccountGroup')
            : categoryAccountGroup;
        const hasGroupLabel = categoryAccountGroup && categoryAccountGroup !== 'NonPalCodeAccountGroup';
        const isFirstCategoryInClassification = catIndex === 0;

        let detailLabel = agLabel;
        if (hasGroupLabel) {
          if (categoryLabel.startsWith(groupLabel)) {
            const categorySuffix = categoryLabel.slice(groupLabel.length).replace(/^[\s\-–—]+/, '');
            detailLabel = categorySuffix ? `${groupLabel} - ${categorySuffix}` : groupLabel;
          } else {
            detailLabel = `${groupLabel} - ${categoryLabel}`;
          }
        }

        const headerHtml = isFirstCategoryInClassification
          ? `
          <div class="combined-section-header accountingGroupClassification_accountingGroup_container">
              <div class="sums-of-transactions section-header-ag-label">
                  ${agLabel}
              </div>
              <div class="sums-of-transactions section-header-detail-label">
                  ${detailLabel}
              </div>
          </div>
        `
          : `
          <div class="combined-section-header combined-section-header-continued accountingGroupClassification_accountingGroup_container">
              <div class="sums-of-transactions section-header-detail-label">
                  ${detailLabel}
              </div>
          </div>
        `;

        sectionContent += generatePalCodeTables(
          transactionsForCat,
          catKey,
          headerHtml,
          palCodeAccountGroup,
          comparisonTransactions,
          comparisonTotalIncome,
          firstPeriodData,
          secondPeriodData,
          isLastCategoryInGroup
        );
      });
    });

    return sectionContent;
  };

  // Foreign-currency columns per period: detailed (showCards) -> חובה/זכות/יתרה מט"ח (3),
  // not detailed -> יתרה מט"ח (1). Zero when no currency is selected.
  const getFcColsPerPeriod = () => {
    if (fcOnly) return showCards ? 3 : 1;
    return showFc ? (showCards ? 3 : 1) : 0;
  };

  const getShekelColsPerPeriod = () => {
    if (fcOnly) return 0;
    return 1 + (showPercentageColumn ? 1 : 0);
  };

  const renderPlFcHeaderCells = () => {
    if (!showFc) return '';
    if (showCards) {
      return `
        <th class="td-header td-fc">חובה ${fcLabel}</th>
        <th class="td-header td-fc">זכות ${fcLabel}</th>
        <th class="td-header td-fc">יתרה ${fcLabel}</th>`;
    }
    return `<th class="td-header td-fc">יתרה ${fcLabel}</th>`;
  };

  const renderPlFcRowCells = (tr) => {
    if (!showFc) return '';
    const fmt = (v) => beautifyNumber(Number(v) || 0);
    if (showCards) {
      return `
        <td class="td-amount-cell td-fc">${fmt(tr?.foreignCurrencyDebit)}</td>
        <td class="td-amount-cell td-fc">${fmt(tr?.foreignCurrencyCredit)}</td>
        <td class="td-amount-cell td-fc">${fmt(tr?.foreignCurrencyBalance)}</td>`;
    }
    return `<td class="td-amount-cell td-fc">${fmt(tr?.foreignCurrencyBalance)}</td>`;
  };

  const renderShekelHeaderCells = (withUnderline = false) => {
    if (fcOnly) return '';
    const cls = withUnderline ? 'td-header td-header-sub-amount' : 'td-header';
    const label = withUnderline
      ? wrapHeaderLabelWithUnderline('שקלים חדשים')
      : 'שקלים חדשים';
    const pctCls = withUnderline ? 'td-header td-header-sub-pct' : 'td-header';
    const pctLabel = withUnderline
      ? wrapHeaderLabelWithUnderline('מהמכירות%')
      : '%מהמכירות';
    return `
      <th class="${cls}">${label}</th>
      ${showPercentageColumn ? `<th class="${pctCls}">${pctLabel}</th>` : ''}`;
  };

  const renderShekelRowCells = (balance, percentage) => {
    if (fcOnly) return '';
    return `
      <td class="td-amount-cell">${beautifyNumber(balance)}</td>
      ${showPercentageColumn ? `<td class="td-percentage">${percentage}</td>` : ''}`;
  };

  const getTableNumericColumnCount = (secondPeriodData = null) => {
    let count = getShekelColsPerPeriod() + getFcColsPerPeriod();
    if (showPeriodComparison && secondPeriodData) {
      count += getShekelColsPerPeriod() + getFcColsPerPeriod();
    }
    return count;
  };

  const buildTableColGroup = (secondPeriodData = null) => {
    const numericCols = Array.from(
      { length: getTableNumericColumnCount(secondPeriodData) },
      () => '<col class="col-numeric" />'
    ).join('');
    return `<colgroup><col class="col-account" />${numericCols}</colgroup>`;
  };

  const getTableLayoutClass = (secondPeriodData = null) => {
    if (showPeriodComparison && secondPeriodData) {
      const densityClass = !showPercentageColumn ? ' pl-cols-dual' : ' pl-cols-quad';
      return ` pl-period-comparison${densityClass}`;
    }
    return ' pl-single-period';
  };

  const wrapHeaderLabelWithUnderline = (text) => `
    <span class="header-label-underline">
      <span class="header-label-value">${text}</span>
      <span class="accounting-underline-lines accounting-underline-lines-single">
        <span class="accounting-underline-line"></span>
      </span>
    </span>
  `;

  const getTableColSpan = (secondPeriodData = null) =>
    1 + getTableNumericColumnCount(secondPeriodData);

  const buildTableThead = (firstPeriodData = null, secondPeriodData = null) => {
    const accountHeader = showCards ? 'כרטיס חשבון' : '';
    const periodColSpan = getShekelColsPerPeriod() + getFcColsPerPeriod();

    if (showPeriodComparison && secondPeriodData && firstPeriodData) {
      const period1Label = formatDateRange(
        firstPeriodData.startDate,
        firstPeriodData.endDate,
        'מתקופה',
      );
      const period2Label = formatDateRange(
        secondPeriodData.startDate,
        secondPeriodData.endDate,
        'לתקופה',
      );

      return `
        <thead>
            <tr class="tr tr-header-period-row">
                <th class="td-header-1 td-header-account" rowspan="2">${accountHeader}</th>
                <th class="td-header td-header-period" colspan="${periodColSpan}"><span class="header-label-value">${period1Label}</span></th>
                <th class="td-header td-header-period" colspan="${periodColSpan}"><span class="header-label-value">${period2Label}</span></th>
            </tr>
            <tr class="tr tr-header-sub-row">
                ${renderShekelHeaderCells(true)}
                ${renderPlFcHeaderCells()}
                ${renderShekelHeaderCells(true)}
                ${renderPlFcHeaderCells()}
            </tr>
        </thead>
      `;
    }

    return `
        <thead>
            <tr class="tr">
                <th class="td-header-1">${accountHeader}</th>
                ${renderShekelHeaderCells()}
                ${renderPlFcHeaderCells()}
            </tr>
        </thead>
    `;
  };

  const generatePalCodeTables = (
    transactionsForCategory,
    palCodeCategory,
    headerHtml,
    accountingGroupName,
    comparisonTransactions = null,
    comparisonTotalIncome = 0,
    firstPeriodData = null,
    secondPeriodData = null,
    isLastCategoryInGroup = false
  ) => {
    let content = '';
    const groupedByPalCode = groupBy(transactionsForCategory, 'palCode');

    // Start table wrapped in pageStyle div
    content += `
    <div class="pageStyle">
    <div class="section-header-block">
        ${headerHtml}
    </div>
    <table class="table${getTableLayoutClass(secondPeriodData)}"  dir="rtl">
        ${buildTableColGroup(secondPeriodData)}
        ${buildTableThead(firstPeriodData, secondPeriodData)}
        <tbody>
`;

    let rowsCount = 0;
    const palCodes = Object.keys(groupedByPalCode);
    
    palCodes.forEach((palCode, index) => {
      const transactions = groupedByPalCode[palCode];
      
      // Insert a row for the palCode
      content += `
        <tr>
          <td class="overflowed" colspan="${getTableColSpan(secondPeriodData)}">
            ${palCode === 'NonPalCode' ? t('NonPalCode') : palCode}
          </td>
        </tr>
      `;
      
      // Get comparison transactions for this palCode if in comparison mode
      const palCodeComparisonTransactions = showPeriodComparison && comparisonTransactions ? 
        comparisonTransactions.filter(ctr => ctr.palCode === palCode) : [];

      // Process transactions and accumulate totals
      transactions.forEach((firstTr, index) => {
        const secondTr = palCodeComparisonTransactions.find(ctr => 
          ctr.accountCardId === firstTr.accountCardId && ctr.palCode === firstTr.palCode
        );
        
        // Calculate totals for first period
        profitAndLossTotalInitDebit += firstTr.initDebitAccount;
        profitAndLossTotalInitCredit += firstTr.initCreditAccount;
        profitAndLossTotalDebit += firstTr.debitAccount;
        profitAndLossTotalCredit += firstTr.creditAccount;
        profitAndLossTotalBalance1 += firstTr.balance;
        if (showFc) {
          profitAndLossTotalFcDebit += Number(firstTr.foreignCurrencyDebit) || 0;
          profitAndLossTotalFcCredit += Number(firstTr.foreignCurrencyCredit) || 0;
          profitAndLossTotalFcBalance += Number(firstTr.foreignCurrencyBalance) || 0;
        }

        // Accumulate total
        totalInitDebitAccountReport += firstTr.initDebitAccount;
        totalInitCreditAccountReport += firstTr.initCreditAccount;
        totalDebitAccountReport += firstTr.debitAccount;
        totalCreditAccountReport += firstTr.creditAccount;
        totalBalanceReport += firstTr.balance;
        
        // Calculate totals for second period
        if (showPeriodComparison && secondTr) {
          comparisonProfitAndLossTotalInitDebit += secondTr.initDebitAccount;
          comparisonProfitAndLossTotalInitCredit += secondTr.initCreditAccount;
          comparisonProfitAndLossTotalDebit += secondTr.debitAccount;
          comparisonProfitAndLossTotalCredit += secondTr.creditAccount;
          comparisonProfitAndLossTotalBalance += secondTr.balance;
          if (showFc) {
            comparisonProfitAndLossTotalFcDebit += Number(secondTr.foreignCurrencyDebit) || 0;
            comparisonProfitAndLossTotalFcCredit += Number(secondTr.foreignCurrencyCredit) || 0;
            comparisonProfitAndLossTotalFcBalance += Number(secondTr.foreignCurrencyBalance) || 0;
          }

          comparisonTotalInitDebitAccountReport += secondTr.initDebitAccount;
          comparisonTotalInitCreditAccountReport += secondTr.initCreditAccount;
          comparisonTotalDebitAccountReport += secondTr.debitAccount;
          comparisonTotalCreditAccountReport += secondTr.creditAccount;
          comparisonTotalBalanceReport += secondTr.balance;
        }
        
        const displayTr = firstTr;
        const transactionDetail = encodeURIComponent(JSON.stringify(displayTr));

        const firstPeriodBalance = firstTr.balance;
        const secondPeriodBalance = secondTr ? secondTr.balance : 0;
        
        const firstPeriodPercentage = totalIncome > 0 ? 
          ((firstPeriodBalance / totalIncome) * 100).toFixed(2) + '%' : 
          '0.00%';
          
        const secondPeriodPercentage = secondTr && comparisonTotalIncome > 0 ? 
          ((secondPeriodBalance / comparisonTotalIncome) * 100).toFixed(2) + '%' : 
          '0.00%';

        if (showCards) {
          content += `
          <tr class="hoverTr" style="cursor:pointer"
              onclick="window.parent.dispatchEvent(new CustomEvent('rowClick', { detail: '${transactionDetail}' }))">
            <td class="td-account-cell">${formatAccountCardLabel(displayTr.accountCardId, displayTr.accountCardName)}</td>
            ${renderShekelRowCells(firstPeriodBalance, firstPeriodPercentage)}
            ${renderPlFcRowCells(firstTr)}
            ${showPeriodComparison ? `
                ${renderShekelRowCells(secondPeriodBalance, secondPeriodPercentage)}
                ${renderPlFcRowCells(secondTr)}
            ` : ''}
          </tr>
        `;
        }

        rowsCount++;
        // Paginate if we reach transactionsPerPage
        if (rowsCount >= transactionsPerPage) {
          content += `
            </tbody>
            </table>
          </div>
          <div class="pageStyle">
                <div class="section-header-block">
                    ${headerHtml}
                </div>
                <table class="table${getTableLayoutClass(secondPeriodData)}" dir="rtl">
                    ${buildTableColGroup(secondPeriodData)}
                    ${buildTableThead(firstPeriodData, secondPeriodData)}
                    <tbody>
          `;
          rowsCount = 0;
        }
      });

      // Add a subtotal for this palCode
      content += TableTotals.generatePalCodeTotals(
        transactions, 
        palCode, 
        showPeriodComparison,
        comparisonTransactions, 
        comparisonTotalIncome, 
        totalIncome,
        showPercentageColumn,
        t,
        { showFc, showCards, selectedCurrency, fcOnly }
      );
    });

    // A total row for the entire palCodeCategory
    content += TableTotals.generatePalCodeCategoryTotals(
      transactionsForCategory, 
      palCodeCategory, 
      accountingGroupName, 
      showPeriodComparison,
      comparisonTransactions, 
      comparisonTotalIncome,
      totalIncome,
      showPercentageColumn,
      profitAndLossTotalBalance1,
      comparisonProfitAndLossTotalBalance,
      firstPeriodData,
      t,
      isLastCategoryInGroup,
      {
        showFc,
        showCards,
        selectedCurrency,
        fcOnly,
        plFc: {
          debit: profitAndLossTotalFcDebit,
          credit: profitAndLossTotalFcCredit,
          balance: profitAndLossTotalFcBalance,
        },
        compPlFc: {
          debit: comparisonProfitAndLossTotalFcDebit,
          credit: comparisonProfitAndLossTotalFcCredit,
          balance: comparisonProfitAndLossTotalFcBalance,
        },
      }
    );

    // Close the table and pageStyle div
    content += `
          </tbody>
        </table>
    </div>
    `;

    return content;
  };

  // Event handlers
  // Grouped, styled, RTL Excel export that mirrors the on-screen / PDF report:
  // same sections, row order, column set, totals and toggle behaviour.
  const handleDownloadExcel = async () => {
    if (!data || !data.transaction) return;

    const plTransactions = filteredTransactions.length > 0
      ? filteredTransactions
      : data.transaction.filter(tr => profitAndLossNames.includes(tr.palCodeAccountGroup));
    if (!plTransactions.length) return;

    /* ---- Period comparison availability (mirror generateReportHtml) ---- */
    const showComparison = !!(showPeriodComparison && periodComparisonData?.data1 && periodComparisonData?.data2);
    const firstPeriodData = showComparison ? periodComparisonData.data1 : data;
    const secondPeriodData = showComparison ? periodComparisonData.data2 : null;

    // When a single currency is selected, only rows with FC activity are shown (mirror sectionTransactions).
    const sourceTx = fcOnly ? plTransactions.filter(fcRowHasActivity) : plTransactions;
    const compSource = (fcOnly && comparisonTransactions)
      ? comparisonTransactions.filter(fcRowHasActivity)
      : (comparisonTransactions || []);
    if (!sourceTx.length) return;

    /* ---- Numeric helpers ---- */
    const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
    const calcPct = (value, income) => (income <= 0 ? '0.00%' : ((value / income) * 100).toFixed(2) + '%');
    const sumBalance = (txns) => (txns || []).reduce((acc, tr) => acc + (Number(tr.balance) || 0), 0);
    const sumFc = (txns, f) => (txns || []).reduce((acc, tr) => acc + (Number(tr?.[f]) || 0), 0);

    /* ---- Column model (mirror header renderers) ---- */
    const shekelColsPerPeriod = fcOnly ? 0 : (1 + (showPercentageColumn ? 1 : 0));
    const fcColsPerPeriod = !showFc ? 0 : (showCards ? 3 : 1);
    const perPeriodCols = shekelColsPerPeriod + fcColsPerPeriod;
    const colCount = 1 + perPeriodCols + (showComparison ? perPeriodCols : 0);

    // Per-period column value builders. Percentage cells are STRINGS; balances/FC are numbers.
    const shekelVals = (balance, pct) =>
      fcOnly ? [] : [round2(balance), ...(showPercentageColumn ? [pct] : [])];
    const fcVals = (tr) =>
      !showFc ? [] : (showCards
        ? [round2(tr?.foreignCurrencyDebit), round2(tr?.foreignCurrencyCredit), round2(tr?.foreignCurrencyBalance)]
        : [round2(tr?.foreignCurrencyBalance)]);
    const fcSumVals = (txns) =>
      !showFc ? [] : (showCards
        ? [round2(sumFc(txns, 'foreignCurrencyDebit')), round2(sumFc(txns, 'foreignCurrencyCredit')), round2(sumFc(txns, 'foreignCurrencyBalance'))]
        : [round2(sumFc(txns, 'foreignCurrencyBalance'))]);
    const fcRunningVals = (fcObj) =>
      !showFc ? [] : (showCards
        ? [round2(fcObj.debit), round2(fcObj.credit), round2(fcObj.balance)]
        : [round2(fcObj.balance)]);
    const fcBlankVals = () => (!showFc ? [] : (showCards ? ['', '', ''] : ['']));

    // Per-period type map (for number formatting): 'amount' | 'pct'.
    const periodColTypes = [];
    if (!fcOnly) {
      periodColTypes.push('amount');
      if (showPercentageColumn) periodColTypes.push('pct');
    }
    if (showFc) {
      if (showCards) periodColTypes.push('amount', 'amount', 'amount');
      else periodColTypes.push('amount');
    }
    const colTypes = ['account', ...periodColTypes, ...(showComparison ? periodColTypes : [])];

    /* ---- Header labels (mirror renderShekelHeaderCells / renderPlFcHeaderCells) ---- */
    // Prefer the period label for the amount column so Excel doesn't repeat a generic
    // "שקלים חדשים" across periods (matches server SortCode P&L Excel organization).
    const fcHdr = !showFc ? [] : (showCards
      ? [`חובה ${fcLabel}`, `זכות ${fcLabel}`, `יתרה ${fcLabel}`]
      : [`יתרה ${fcLabel}`]);
    const accountHeader = showCards ? 'כרטיס חשבון' : '';

    const buildPeriodHeaders = (periodLabel) => {
      const hdr = [];
      if (!fcOnly) {
        hdr.push(periodLabel || 'שקלים חדשים');
        if (showPercentageColumn) hdr.push('%מהמכירות');
      }
      hdr.push(...fcHdr);
      return hdr;
    };

    let headerCells;
    if (showComparison) {
      const p1Label = formatDateRange(firstPeriodData?.startDate, firstPeriodData?.endDate, 'מתקופה');
      const p2Label = formatDateRange(secondPeriodData?.startDate, secondPeriodData?.endDate, 'לתקופה');
      headerCells = [accountHeader, ...buildPeriodHeaders(p1Label), ...buildPeriodHeaders(p2Label)];
    } else {
      const rangeLabel = formatDateRange(firstPeriodData?.startDate, firstPeriodData?.endDate, 'לתקופה');
      headerCells = [accountHeader, ...buildPeriodHeaders(rangeLabel)];
    }

    /* ---- Build the report as an ordered list of row descriptors ---- */
    const items = [];

    // Title block.
    items.push({ type: 'merged', kind: 'clientLine', text: String(data.clientName || '') });
    items.push({ type: 'merged', kind: 'reportTitle', text: t('ProfitAndLossReport') });
    const dateText = formatDateRange(firstPeriodData?.startDate ?? data?.startDate, firstPeriodData?.endDate ?? data?.endDate);
    if (dateText) items.push({ type: 'merged', kind: 'dateLine', text: dateText });
    items.push({ type: 'blank' });

    // Column header once at the top (avoid repeating per section).
    items.push({ type: 'header', kind: 'header' });

    // Running P&L accumulators (mirror module-level accumulators, but local so HTML state is untouched).
    let plBalance = 0, plFcDebit = 0, plFcCredit = 0, plFcBalance = 0;
    let compPlBalance = 0, compPlFcDebit = 0, compPlFcCredit = 0, compPlFcBalance = 0;

    const byClass = groupBy(sourceTx, 'accountingGroupClassification');
    Object.keys(byClass).forEach((agClassification) => {
      const clsTx = byClass[agClassification];
      const classAccountGroup = clsTx[0]?.palCodeAccountGroup || '';
      const byCat = groupBy(clsTx, 'palCodeCategory');
      const catKeys = Object.keys(byCat);

      catKeys.forEach((catKey, catIndex) => {
        const catTx = byCat[catKey];
        const isLastCategoryInGroup = catIndex === catKeys.length - 1;
        const isFirstCategoryInClassification = catIndex === 0;

        const categoryLabel = catKey === 'NonPalCodeCategory' ? t('NonPalCodeCategory') : catKey;
        const agLabel = agClassification === 'NonAccountingGroupClassifaction'
          ? t('NonAccountingGroupClassifaction')
          : agClassification;
        const categoryAccountGroup = catTx[0]?.palCodeAccountGroup || classAccountGroup;
        const groupLabel = categoryAccountGroup === 'NonPalCodeAccountGroup'
          ? t('NonPalCodeAccountGroup')
          : categoryAccountGroup;
        const hasGroupLabel = categoryAccountGroup && categoryAccountGroup !== 'NonPalCodeAccountGroup';

        let detailLabel = agLabel;
        if (hasGroupLabel) {
          if (categoryLabel.startsWith(groupLabel)) {
            const categorySuffix = categoryLabel.slice(groupLabel.length).replace(/^[\s\-–—]+/, '');
            detailLabel = categorySuffix ? `${groupLabel} - ${categorySuffix}` : groupLabel;
          } else {
            detailLabel = `${groupLabel} - ${categoryLabel}`;
          }
        }

        // Section header row(s).
        if (isFirstCategoryInClassification) {
          items.push({ type: 'merged', kind: 'agLabel', text: agLabel });
        }
        items.push({ type: 'merged', kind: 'detailLabel', text: detailLabel });

        // palCode groups.
        const byPal = groupBy(catTx, 'palCode');
        Object.keys(byPal).forEach((palCode) => {
          const palTx = byPal[palCode];
          const palLabel = palCode === 'NonPalCode' ? t('NonPalCode') : palCode;

          // Full-width palCode label row (always emitted, matches the HTML `overflowed` row).
          items.push({ type: 'merged', kind: 'palCodeLabel', text: palLabel });

          const compPalTx = showComparison ? compSource.filter(ctr => ctr.palCode === palCode) : [];

          palTx.forEach((firstTr, idx) => {
            const secondTr = compPalTx.find(ctr =>
              ctr.accountCardId === firstTr.accountCardId && ctr.palCode === firstTr.palCode);

            // Accumulate running totals for EVERY transaction (independent of showCards).
            plBalance += Number(firstTr.balance) || 0;
            if (showFc) {
              plFcDebit += Number(firstTr.foreignCurrencyDebit) || 0;
              plFcCredit += Number(firstTr.foreignCurrencyCredit) || 0;
              plFcBalance += Number(firstTr.foreignCurrencyBalance) || 0;
            }
            if (showComparison && secondTr) {
              compPlBalance += Number(secondTr.balance) || 0;
              if (showFc) {
                compPlFcDebit += Number(secondTr.foreignCurrencyDebit) || 0;
                compPlFcCredit += Number(secondTr.foreignCurrencyCredit) || 0;
                compPlFcBalance += Number(secondTr.foreignCurrencyBalance) || 0;
              }
            }

            // Card rows are only emitted when showCards (matches HTML).
            if (showCards) {
              const b1 = Number(firstTr.balance) || 0;
              const pct1 = calcPct(b1, totalIncome);
              const periods = [[...shekelVals(b1, pct1), ...fcVals(firstTr)]];
              if (showComparison) {
                const b2 = secondTr ? (Number(secondTr.balance) || 0) : 0;
                const pct2 = calcPct(b2, comparisonTotalIncome);
                periods.push([...shekelVals(b2, pct2), ...fcVals(secondTr || {})]);
              }
              items.push({
                type: 'row',
                kind: idx % 2 === 1 ? 'stripe' : 'data',
                accountLabel: formatAccountCardLabel(firstTr.accountCardId, firstTr.accountCardName),
                periods,
              });
            }
          });

          // palCode subtotal (mirror TableTotals.generatePalCodeTotals).
          const subBal = sumBalance(palTx);
          const subPeriods = [[...shekelVals(subBal, calcPct(subBal, totalIncome)), ...fcSumVals(palTx)]];
          if (showComparison) {
            const cSubBal = sumBalance(compPalTx);
            subPeriods.push([...shekelVals(cSubBal, calcPct(cSubBal, comparisonTotalIncome)), ...fcSumVals(compPalTx)]);
          }
          items.push({
            type: 'row',
            kind: 'subtotal',
            accountLabel: `סה''כ: ${palLabel} `,
            periods: subPeriods,
          });
        });

        // Category total (mirror TableTotals.generatePalCodeCategoryTotals — double underline tier).
        const catBal = sumBalance(catTx);
        const catPeriods = [[...shekelVals(catBal, calcPct(catBal, totalIncome)), ...fcSumVals(catTx)]];
        const compCatTx = showComparison ? compSource.filter(ctr => ctr.palCodeCategory === catKey) : [];
        if (showComparison) {
          const cCatBal = sumBalance(compCatTx);
          catPeriods.push([...shekelVals(cCatBal, calcPct(cCatBal, comparisonTotalIncome)), ...fcSumVals(compCatTx)]);
        }
        items.push({
          type: 'row',
          kind: 'categoryTotal',
          accountLabel: `סה''כ: ${categoryLabel}`,
          periods: catPeriods,
        });

        if (isLastCategoryInGroup) {
          const accountingGroupName = classAccountGroup;
          const isIncomeGroup = accountingGroupName === 'הכנסות';

          // Group total (triple tier) — first period from RAW firstPeriodData.transaction.
          const rawGroupTx = (firstPeriodData?.transaction || []).filter(tr => tr.palCodeAccountGroup === accountingGroupName);
          const groupTx = fcOnly ? rawGroupTx.filter(fcRowHasActivity) : rawGroupTx;
          const groupBal = sumBalance(groupTx);
          const groupPeriods = [[...shekelVals(groupBal, calcPct(groupBal, totalIncome)), ...fcSumVals(groupTx)]];
          const compGroupTx = showComparison ? compSource.filter(tr => tr.palCodeAccountGroup === accountingGroupName) : [];
          if (showComparison) {
            const cGroupBal = sumBalance(compGroupTx);
            groupPeriods.push([...shekelVals(cGroupBal, calcPct(cGroupBal, comparisonTotalIncome)), ...fcSumVals(compGroupTx)]);
          }
          items.push({
            type: 'row',
            kind: 'groupTotal',
            accountLabel: `סה''כ ${accountingGroupName}:`,
            periods: groupPeriods,
          });

          // Running "profit & loss after <group>" row (mirror TableTotals third row).
          // Period 1: income group => empty balance & FC, but pct still shown; otherwise running values.
          const runP1 = [];
          if (!fcOnly) {
            runP1.push(isIncomeGroup ? '' : round2(plBalance));
            if (showPercentageColumn) runP1.push(calcPct(plBalance, totalIncome));
          }
          if (showFc) {
            (isIncomeGroup ? fcBlankVals() : fcRunningVals({ debit: plFcDebit, credit: plFcCredit, balance: plFcBalance }))
              .forEach(v => runP1.push(v));
          }
          const runPeriods = [runP1];
          if (showComparison) {
            // Comparison: balance ALWAYS shown; FC empty for income group.
            const runP2 = [];
            if (!fcOnly) {
              runP2.push(round2(compPlBalance));
              if (showPercentageColumn) runP2.push(calcPct(compPlBalance, comparisonTotalIncome));
            }
            if (showFc) {
              (isIncomeGroup ? fcBlankVals() : fcRunningVals({ debit: compPlFcDebit, credit: compPlFcCredit, balance: compPlFcBalance }))
                .forEach(v => runP2.push(v));
            }
            runPeriods.push(runP2);
          }
          items.push({
            type: 'row',
            kind: 'runningPl',
            accountLabel: isIncomeGroup ? '' : `סה''כ רווח והפסד לאחר ${accountingGroupName}:`,
            periods: runPeriods,
            isRunningPl: true,
          });
        }
      });
    });

    // The HTML strips the LAST running "profit & loss after" row and replaces it with the
    // grand-total row (generateFinalSummaryRow). Reproduce that exactly.
    for (let i = items.length - 1; i >= 0; i -= 1) {
      if (items[i].isRunningPl) {
        items.splice(i, 1);
        break;
      }
    }

    // Grand total (mirror TableTotals.generateFinalSummaryRow — strongest tier).
    const finalPeriods = [[
      ...shekelVals(plBalance, calcPct(plBalance, totalIncome)),
      ...fcRunningVals({ debit: plFcDebit, credit: plFcCredit, balance: plFcBalance }),
    ]];
    if (showComparison) {
      finalPeriods.push([
        ...shekelVals(compPlBalance, calcPct(compPlBalance, comparisonTotalIncome)),
        ...fcRunningVals({ debit: compPlFcDebit, credit: compPlFcCredit, balance: compPlFcBalance }),
      ]);
    }
    items.push({
      type: 'row',
      kind: 'grandTotal',
      accountLabel: `סה''כ רווח והפסד בדוח: `,
      periods: finalPeriods,
    });

    /* ---- Styles (visual language borrowed from the Account Card Excel export) ---- */
    const PALETTE = {
      primary: 'FF2F4BFF',
      primaryDark: 'FF1E308F',
      headerFont: 'FFFFFFFF',
      palFill: 'FFEFF2FF',
      subFill: 'FFF6F8FF',
      catFill: 'FFDDE4FF',
      groupFill: 'FFD7DFFF',
      grandFill: 'FF1E308F',
      stripe: 'FFF6F8FF',
      text: 'FF1F2937',
      border: 'FFC3CBDB',
    };
    const thinBorder = { style: 'thin', color: { rgb: PALETTE.border } };
    const allThin = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
    const STYLES = {
      clientLine: { font: { bold: true, sz: 13, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right' } },
      reportTitle: { font: { bold: true, sz: 20, color: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center' } },
      dateLine: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
      agLabel: { font: { bold: true, sz: 14, color: { rgb: PALETTE.primaryDark } }, alignment: { horizontal: 'right', vertical: 'center' } },
      detailLabel: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' } },
      header: { font: { bold: true, sz: 12, color: { rgb: PALETTE.headerFont } }, fill: { fgColor: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: allThin },
      palCodeLabel: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.palFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      data: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      stripe: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      subtotal: { font: { bold: true, sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.subFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      categoryTotal: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.catFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      groupTotal: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.groupFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      runningPl: { font: { bold: true, sz: 12, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.groupFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
      grandTotal: { font: { bold: true, sz: 13, color: { rgb: PALETTE.headerFont } }, fill: { fgColor: { rgb: PALETTE.grandFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
    };
    const ROW_HEIGHT = {
      clientLine: 22, reportTitle: 32, dateLine: 20, blank: 8,
      agLabel: 24, detailLabel: 20, header: 30, palCodeLabel: 22,
      data: 18, stripe: 18, subtotal: 20, categoryTotal: 22, groupTotal: 24, runningPl: 22, grandTotal: 26,
    };

    /* ---- Materialize row descriptors into an AOA ---- */
    const aoa = [];
    const merges = [];
    const rowMeta = [];
    const blankRow = () => new Array(colCount).fill('');
    items.forEach((it) => {
      const r = aoa.length;
      if (it.type === 'blank') {
        aoa.push(blankRow());
        rowMeta.push({ kind: 'blank' });
        return;
      }
      if (it.type === 'merged') {
        const row = blankRow();
        row[0] = it.text ?? '';
        aoa.push(row);
        rowMeta.push({ kind: it.kind });
        merges.push({ s: { r, c: 0 }, e: { r, c: Math.max(colCount - 1, 0) } });
        return;
      }
      if (it.type === 'header') {
        const row = [...headerCells];
        while (row.length < colCount) row.push('');
        aoa.push(row);
        rowMeta.push({ kind: 'header' });
        return;
      }
      // data / total rows
      const row = [it.accountLabel ?? ''];
      (it.periods || []).forEach((vals) => vals.forEach((v) => row.push(v)));
      while (row.length < colCount) row.push('');
      aoa.push(row);
      rowMeta.push({ kind: it.kind });
    });

    /* ---- Create worksheet ---- */
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = merges;
    ws['!cols'] = [
      { wch: 34 },
      ...Array.from({ length: Math.max(colCount - 1, 0) }, () => ({ wch: 18 })),
    ];
    ws['!rows'] = rowMeta.map((m) => ({ hpt: ROW_HEIGHT[m.kind] || 18 }));
    ws['!views'] = [{ RTL: true }];

    const mergedKinds = ['clientLine', 'reportTitle', 'dateLine', 'agLabel', 'detailLabel', 'palCodeLabel'];
    aoa.forEach((row, r) => {
      const meta = rowMeta[r];
      if (!meta || meta.kind === 'blank') return;
      if (mergedKinds.includes(meta.kind)) {
        const addr = XLSX.utils.encode_cell({ r, c: 0 });
        if (ws[addr]) ws[addr].s = STYLES[meta.kind];
        return;
      }
      const style = STYLES[meta.kind] || STYLES.data;
      for (let c = 0; c < colCount; c += 1) {
        const addr = XLSX.utils.encode_cell({ r, c });
        let cell = ws[addr];
        if (!cell) { cell = { t: 's', v: '' }; ws[addr] = cell; }
        cell.s = style;
        if (colTypes[c] === 'amount' && typeof cell.v === 'number') cell.z = AMOUNT_NUMBER_FORMAT;
      }
    });

    appendPartnersEquityToSheet(ws, partners, plBalance);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ProfitAndLoss');
    applyWorkbookRtl(wb);
    try {
      await translateWb(wb);
    } catch {
      return;
    }
    XLSX.writeFile(wb, `${data.clientName}_ProfitAndLossReport.xlsx`);
  };

  const downloadPdfBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName || `${data.clientName}-${data.clientCase}_ProfitAndLoss.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const askAddSignature = async ({ questionKey, questionFallback, cancelKey, cancelFallback }) => {
    const result = await Swal.fire({
      title: t('AddSignatureTitle') || 'Add Signature?',
      text: t(questionKey) || questionFallback,
      icon: 'question',
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonColor: '#667eea',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('YesAddSignature') || 'Yes, Add Signature',
      cancelButtonText: t(cancelKey) || cancelFallback,
      reverseButtons: true,
      allowOutsideClick: true,
      allowEscapeKey: true,
      didOpen: () => {
        const closeButton = document.querySelector('.swal2-close');
        if (closeButton) {
          closeButton.style.setProperty('right', 'auto', 'important');
          closeButton.style.setProperty('left', '10px', 'important');
        }
      }
    });
    return result;
  };

  const handleDownloadPdf = async () => {
    try {
      const cleanedHtml = reportHtmlContent
        .replace(/border: 1px solid #ccc;/g, '')
        .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '')
        .replace(/\s*class="hoverTr"/g, '')
        .replace(/\s*hoverTr\s*/g, '');

      const { blob, fileName } =
        await ReportsService.convertHtmlToPdf(client?.caseId, await translateHtml(cleanedHtml), token);

      downloadPdfBlob(blob, fileName);
    } catch (err) {
      console.error('HTML‑to‑PDF failed:', err);
      toast.error(t('ErrorDownloadingPdf') || 'Error downloading PDF');
    }
  };

  // Download PDF - Ask if user wants to add signature + date
  const handleDownloadPdfClick = async () => {
    const result = await askAddSignature({
      questionKey: 'AddSignatureQuestionDownload',
      questionFallback: 'Do you want to add a digital signature and date to this document before downloading?',
      cancelKey: 'NoDownloadWithout',
      cancelFallback: 'No, Download Without Signature',
    });

    if (result.isConfirmed) {
      setSignaturePurpose('download');
      setShowSignatureModal(true);
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      await handleDownloadPdf();
    }
  };

  // Chat functionality - Ask if user wants to add signature
  const handleSendToChatClick = async () => {
    const result = await askAddSignature({
      questionKey: 'AddSignatureQuestion',
      questionFallback: 'Do you want to add a digital signature to this document before sending?',
      cancelKey: 'NoSendWithout',
      cancelFallback: 'No, Send Without Signature',
    });

    if (result.isConfirmed) {
      setSignaturePurpose('chat');
      setShowSignatureModal(true);
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      await handleSendToChat();
    }
    // If user closes by clicking outside, ESC, or X button - do nothing
  };

  // Helper function to generate HTML with signature (synchronous)
  const generateHtmlWithSignature = (signatureDataUrl) => {
    if (!data) return "";

    // Create signature section HTML with RTL direction
    const signatureSection = `
<div class="signature-section" style="margin-top: 40px; padding: 20px; page-break-inside: avoid; direction: rtl;">
  <div style="border-top: 2px solid #333; padding-top: 20px;">
    <div style="display: flex; justify-content: space-between; align-items: flex-end; flex-wrap: wrap; gap: 20px; direction: rtl;">
      <div style="flex: 1; min-width: 200px;">
        <div style="text-align: center; margin-bottom: 10px;">
          <img src="${signatureDataUrl}" alt="Signature" style="max-width: 300px; max-height: 100px; border: 1px solid #e0e0e0; padding: 5px; background: white;" />
        </div>
        <div style="border-top: 2px solid #000; padding-top: 5px; text-align: center;">
          <strong>חתימה מורשית</strong>
        </div>
      </div>
      <div style="flex: 1; min-width: 200px; text-align: center;">
        <div style="margin-bottom: 10px;">
          ${new Date().toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        <div style="border-top: 2px solid #000; padding-top: 5px;">
          <strong>תאריך</strong>
        </div>
      </div>
    </div>
  </div>
</div>`;

    // Get the current HTML content and clean it
    let htmlWithoutSignature = reportHtmlContent
      .replace(/border: 1px solid #ccc;/g, '')
      .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '')
      .replace(/\s*class="hoverTr"/g, '')
      .replace(/\s*hoverTr\s*/g, '');

    // Insert signature before closing body tag
    let htmlWithSignature = htmlWithoutSignature;

    // Try multiple patterns to ensure we find the right place
    if (htmlWithSignature.includes('</div>\n      </body>')) {
      htmlWithSignature = htmlWithSignature.replace(
        '</div>\n      </body>',
        `${signatureSection}\n</div>\n      </body>`
      );
    } else if (htmlWithSignature.includes('</div></body>')) {
      htmlWithSignature = htmlWithSignature.replace(
        '</div></body>',
        `${signatureSection}\n</div></body>`
      );
    } else {
      htmlWithSignature = htmlWithSignature.replace(
        '</body>',
        `${signatureSection}\n</body>`
      );
    }

    return htmlWithSignature;
  };

  // Handle signature after user signs (send to chat or download)
  const handleSendWithSignature = async (signature) => {
    try {
      setShowSignatureModal(false);
      const isDownload = signaturePurpose === 'download';

      const loadingToast = toast.info(t('GeneratingSignedPDF') || 'Generating signed PDF...', {
        autoClose: false,
        closeButton: false
      });

      try {
        const htmlWithSignature = await translateHtml(generateHtmlWithSignature(signature));
        const { blob, fileName } = await ReportsService.convertHtmlToPdf(client?.caseId, htmlWithSignature, token);
        const resolvedFileName = fileName || `${data.clientName}-${data.clientCase}_ProfitAndLoss.pdf`;

        if (isDownload) {
          downloadPdfBlob(blob, resolvedFileName);
          toast.dismiss(loadingToast);
          toast.success(t('PdfDownloadedSuccessfully') || 'PDF downloaded successfully.');
          return;
        }

        await selectUserAndOpenChat(client.caseId, client.name || 'Client');
        const file = new File([blob], resolvedFileName, { type: 'application/pdf' });
        await handleSendMessageToChat(file, client.caseId, client.name);

        toast.dismiss(loadingToast);
        toast.success(t('DocumentSentToChat'));
      } catch (innerError) {
        console.error('Error in PDF generation/sending:', innerError);
        toast.dismiss(loadingToast);
        toast.error(isDownload
          ? (t('ErrorDownloadingPdf') || 'Error downloading PDF')
          : t('ErrorSendingToChat'));
      }

    } catch (error) {
      console.error('Error handling signed document:', error);
      toast.error(signaturePurpose === 'download'
        ? (t('ErrorDownloadingPdf') || 'Error downloading PDF')
        : t('ErrorSendingToChat'));
    }
  };

  // Send without signature (original function)
  const handleSendToChat = async () => {
    // Declare loadingToast outside try block so it's accessible in catch
    let loadingToast;
    

    try {
      // Show loading toast
      loadingToast = toast.info(t('GeneratingPDF') || 'Generating PDF...', {
        autoClose: false,
        closeButton: false
      });

      // First, open chat with the client
      await selectUserAndOpenChat(client.caseId, client.name || 'Client');
      
      // Generate PDF content (similar to handleDownloadPdf)
      const cleanedHtml = reportHtmlContent
        .replace(/border: 1px solid #ccc;/g, '')
        .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '')
        .replace(/\s*class="hoverTr"/g, '')
        .replace(/\s*hoverTr\s*/g, '');

      const { blob, fileName } =
        await ReportsService.convertHtmlToPdf(client?.caseId, await translateHtml(cleanedHtml), token);
      
      // Create a file from the blob
      const file = new File([blob], fileName || `${data.clientName}-${data.clientCase}_ProfitAndLoss.pdf`, { type: 'application/pdf' });
      
      // Send the file to chat
      await handleSendMessageToChat(file, client.caseId, client.name);
      
      toast.dismiss(loadingToast);
      toast.success(t('FileSentToChat'));
    } catch (error) {
      console.error('Error sending report to chat:', error);
      if (loadingToast) {
      toast.dismiss(loadingToast);

      }
      toast.error(t('ErrorSendingToChat'));
    }
  };

  const handlePrint = async () => {
    const cleanedHtml = reportHtmlContent
    .replace(/border: 1px solid #ccc;/g, '')
    .replace(/box-shadow: 0px 0px 10px rgba\(0, 0, 0, 0\.1\);/g, '')
    .replace(/\s*class="hoverTr"/g, '')
    .replace(/\s*hoverTr\s*/g, '');
    const printWindow = window.open('', '_blank');
    let html;
    try {
      html = await translateHtml(cleanedHtml);
    } catch {
      printWindow.close();
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const handleShow0CardsCheckbox = (value) => {
    setShowAccountCardsWithZeros(value);
  };

  const handleShowCardsCheckbox = (value) => {
    setCards(value);
  };

  const handleShowAccountCodeIdCheckbox = (value) => {
    setShowAccountCodeId(value);
  };

  const formatAccountCardLabel = (id, name) => {
    const accountName = name || '';
    if (showAccountCodeId && id) {
      return `${id} ${accountName}`.trim();
    }
    return accountName;
  };

  const handleShowPercentageColumnCheckbox = (value) => {
    setShowPercentageColumn(value);
  };

  const loadSegmentationProjects = useCallback(async () => {
    if (!client?.caseId || !token) return;
    setLoadingSegmentations(true);
    try {
      const res = await ProjectsService.getProjects(client.caseId, token);
      if (!res.ok) {
        toast.error(t('ErrorRetrievingProjects'));
        return;
      }
      const raw = await res.json();
      const list = Array.isArray(raw) ? raw : [];
      const mapped = list
        .map((p) => ({
          id: String(p.projectId ?? p.id ?? ''),
          code: p.code ?? '',
          name: p.name ?? '',
          label: `${p.code ?? ''} — ${p.name ?? ''}`,
        }))
        .filter((p) => p.id)
        .sort((a, b) => compareProjectCodes(a, b));
      setSegmentationProjects(mapped);
    } catch (e) {
      console.error(e);
      toast.error(t('ErrorRetrievingProjects'));
    } finally {
      setLoadingSegmentations(false);
    }
  }, [client?.caseId, token, t]);

  const handleByProjectsFilterToggle = async (active) => {
    if (!active) {
      setByProjectsFilterActive(false);
      setSelectedFromSegmentation(null);
      setSelectedToSegmentation(null);
      await notifySegmentationFilterChange(null);
      return;
    }
    setByProjectsFilterActive(true);
    setSelectedFromSegmentation(null);
    setSelectedToSegmentation(null);
    void loadSegmentationProjects();
    await notifySegmentationFilterChange({ trialBalanceType: 'Projects' });
  };

  const handleFromSegmentationChange = async (_, newVal) => {
    if (!byProjectsFilterActive) return;
    let from = newVal;
    let to = selectedToSegmentation;
    if (from && to && compareProjectCodes(from, to) > 0) {
      toast.info(t('ProjectRangeAdjustedToCodeOrder'));
      const tmp = from;
      from = to;
      to = tmp;
      setSelectedToSegmentation(to);
    }
    setSelectedFromSegmentation(from);
    await notifySegmentationFilterChange({
      trialBalanceType: 'Projects',
      ...(from?.id ? { fromSegmentationId: from.id } : {}),
      ...(to?.id ? { toSegmentationId: to.id } : {}),
    });
  };

  const handleToSegmentationChange = async (_, newVal) => {
    if (!byProjectsFilterActive) return;
    let to = newVal;
    let from = selectedFromSegmentation;
    if (from && to && compareProjectCodes(from, to) > 0) {
      toast.info(t('ProjectRangeAdjustedToCodeOrder'));
      const tmp = to;
      to = from;
      from = tmp;
      setSelectedFromSegmentation(from);
    }
    setSelectedToSegmentation(to);
    await notifySegmentationFilterChange({
      trialBalanceType: 'Projects',
      ...(from?.id ? { fromSegmentationId: from.id } : {}),
      ...(to?.id ? { toSegmentationId: to.id } : {}),
    });
  };

  const handleFetchComparisonDataWithRebuild = async () => {
    try {
      const { data1, data2 } = await handleFetchComparisonData();
      rebuildProfitAndLossNames(data1, data2);
    } catch (error) {
      console.error('Error in comparison data fetch:', error);
    }
  };

  const handleCloseComparisonWithRebuild = () => {
    handleCloseComparison();
    if (data && data.transaction?.length > 0) {
      rebuildProfitAndLossNames(data, null);
    }
  };

  // Monthly Report Modal handlers
  const handleOpenMonthlyReportModal = () => {
    setShowMonthlyReportModal(true);
  };

  const handleCloseMonthlyReportModal = () => {
    setShowMonthlyReportModal(false);
  };


  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="profit-and-loss-modal-title"
        aria-describedby="profit-and-loss-modal-description"
      >
        <Box sx={modalStyle}>
          {/* Toolbar */}
          <div style={{ flexShrink: 0, marginBottom: '8px' }}>
            <ReportToolbar
              direction={direction}
              showAccountCardsWithZeros={showAccountCardsWithZeros}
              showCards={showCards}
              showAccountCodeId={showAccountCodeId}
              showPercentageColumn={showPercentageColumn}
              showPeriodComparison={showPeriodComparison}
              onDownloadPdf={handleDownloadPdfClick}
              onDownloadExcel={handleDownloadExcel}
              onPrint={handlePrint}
              onSendToChat={handleSendToChatClick}
              onShowZeroCardsToggle={handleShow0CardsCheckbox}
              onShowCardsToggle={handleShowCardsCheckbox}
              onShowAccountCodeIdToggle={handleShowAccountCodeIdCheckbox}
              onShowPercentageColumnToggle={handleShowPercentageColumnCheckbox}
              onOpenPeriodSelection={handleOpenPeriodSelection}
              onCloseComparison={handleCloseComparisonWithRebuild}
              onOpenMonthlyReport={handleOpenMonthlyReportModal}
              t={t}
              userTypeContext={userTypeContext}
              byProjectsFilterActive={byProjectsFilterActive}
              onByProjectsFilterToggle={handleByProjectsFilterToggle}
              managesProject={client?.managesProject}
              segmentationProjects={segmentationProjects}
              loadingSegmentations={loadingSegmentations}
              selectedFromSegmentation={selectedFromSegmentation}
              selectedToSegmentation={selectedToSegmentation}
              onFromSegmentationChange={handleFromSegmentationChange}
              onToSegmentationChange={handleToSegmentationChange}
              hideLiveDataTools={hideLiveDataTools}
            />
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
              {!hideLiveDataTools && (
                <ReportCurrencySelector
                  selectedCurrency={selectedCurrency}
                  availableCurrencies={availableCurrencies}
                  onCurrencyChange={onCurrencyChange}
                />
              )}
              <ReportLanguageSelector
                reportLanguage={reportLanguage}
                onReportLanguageChange={setReportLanguage}
                isTranslating={isTranslating}
              />
            </div>
          </div>

          {/* The actual rendered HTML */}
          <div
            dir={previewDir}
            dangerouslySetInnerHTML={{ __html: displayHtml }}
            style={{
              width: '100%',
              flex: 1,
              overflowY: 'auto',
              overflowX: 'auto',
              padding: '4px',
              boxSizing: 'border-box',
            }}
          />
        </Box>
      </Modal>

      {/* Period Selection Modal */}
      <PeriodSelectionModal
        open={showPeriodSelectionModal}
        onClose={handleClosePeriodSelection}
        selectedPeriod1={selectedPeriod1}
        selectedPeriod2={selectedPeriod2}
        setSelectedPeriod1={setSelectedPeriod1}
        setSelectedPeriod2={setSelectedPeriod2}
        onFetchComparisonData={handleFetchComparisonDataWithRebuild}
        isLoadingComparison={isLoadingComparison}
        direction={direction}
        t={t}
      />

      {/* Monthly Report Modal */}
      <MonthlyReportModal
        open={showMonthlyReportModal}
        onClose={handleCloseMonthlyReportModal}
        direction={direction}
        t={t}
        // Pass data props from LedgerReport
        palCodesData={palCodesData || []}
        palDates={palDates || []}
        trialBalanceData={data}
        client={client}
        token={token}
        userTypeContext={userTypeContext}
      />

      {/* Signature Modal */}
      <SignatureModal
        open={showSignatureModal}
        handleClose={() => setShowSignatureModal(false)}
        onSendWithSignature={handleSendWithSignature}
        confirmLabel={signaturePurpose === 'download'
          ? (t('SignAndDownload') || 'Sign & Download')
          : undefined}
        reportPreview={`${data?.clientName} - ${t('ProfitAndLossReport') || 'Profit and Loss Report'}`}
      />
    </>
  );
};

export default ProfitAndLossReportHtmlRefactored;
